/* SpecSheetModal — opens a product specification sheet as a popup dialog
   inside the same browser tab (no new window). Fetches the product on demand
   when the dialog opens. Includes Print + Open-in-tab + X-to-close in the
   sticky toolbar. */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "../ui/dialog";
import { X, Printer, ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import SpecSheetBody from "./SpecSheetBody";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpecSheetModal({ open, onOpenChange, infoshopPn, countryCode }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open || !infoshopPn) {
      setProduct(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/infoshop/products/${infoshopPn}`, {
          params: { country_code: countryCode || undefined },
        });
        if (!cancelled) setProduct(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Specification not available.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, infoshopPn, countryCode]);

  // Print only the spec body (not the whole tab)
  const handlePrint = () => {
    const node = bodyRef.current;
    if (!node) {
      window.print();
      return;
    }
    // Print via a hidden same-origin iframe built with DOM APIs (no HTML strings).
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.title = `Spec · ${infoshopPn}`;
    document.querySelectorAll("link[rel='stylesheet'], style").forEach((s) => doc.head.appendChild(doc.importNode(s, true)));
    const wrap = doc.createElement("div");
    wrap.className = "max-w-3xl mx-auto p-8";
    wrap.appendChild(doc.importNode(node, true));
    doc.body.className = "bg-white";
    doc.body.appendChild(wrap);
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => iframe.remove(), 1000);
    }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden p-0 gap-0"
        data-testid="spec-sheet-modal"
        onInteractOutside={(e) => {
          // Keep the dialog persistent during accidental focus-restore clicks.
          e.preventDefault();
        }}
      >
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
              Specification Sheet
            </div>
            <div className="font-mono text-sm font-semibold text-slate-900 truncate">
              {infoshopPn}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handlePrint}
              disabled={!product}
              data-testid="spec-modal-print"
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-3 py-1.5 disabled:opacity-40"
              title="Print / Save as PDF"
            >
              <Printer size={13} /> <span className="hidden sm:inline">Print</span>
            </button>
            {product && (
              <Link
                to={`/infoshop/product/${product.infoshop_pn}/spec`}
                target="_blank"
                rel="noreferrer"
                data-testid="spec-modal-open-tab"
                title="Open in a new tab"
                className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-300 hover:border-slate-900 text-slate-900 rounded-md px-3 py-1.5"
              >
                <ExternalLink size={13} /> <span className="hidden sm:inline">Open in tab</span>
              </Link>
            )}
            <button
              onClick={() => onOpenChange(false)}
              data-testid="spec-modal-close"
              className="h-8 w-8 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 inline-flex items-center justify-center"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 py-8 max-h-[calc(92vh-64px)]">
          {loading && (
            <div className="py-16 text-center text-sm text-slate-500 inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="animate-spin" size={16} /> Loading specification…
            </div>
          )}
          {!loading && error && (
            <div className="py-16 text-center text-sm" data-testid="spec-modal-error">
              <div className="font-display text-xl font-semibold text-slate-900">
                Specification not available
              </div>
              <div className="mt-2 text-slate-600">{error}</div>
            </div>
          )}
          {!loading && product && (
            <div ref={bodyRef}>
              <SpecSheetBody product={product} compact />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
